import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, Calendar, MessageCircle, KeyRound, Link2, Unlink, AlertTriangle, Phone, Copy, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ChangePasswordModal } from "@/components/site/ChangePasswordModal";

export default function AdminSetup() {
  const { user, refreshUser } = useAuth();
  const [calStatus, setCalStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pwdModal, setPwdModal] = useState(false);
  const [waPhone, setWaPhone] = useState(user?.phone || "");
  const [savingPhone, setSavingPhone] = useState(false);
  const [managerPhone, setManagerPhone] = useState(user?.manager_phone || "");
  const [savingManager, setSavingManager] = useState(false);

  const loadStatus = async () => {
    try {
      const r = await api.get("/mio-giorno/calendar/status");
      setCalStatus(r.data);
    } catch (e) { toast.error("Errore stato calendario"); }
  };
  useEffect(() => { loadStatus(); }, []);

  const connectGoogle = async () => {
    setBusy(true);
    try {
      const r = await api.get("/crm/google/authorize");
      window.location.href = r.data.authorize_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore connessione"); setBusy(false); }
  };
  const disconnectGoogle = async () => {
    if (!window.confirm("Scollegare il tuo Google Calendar? Non vedrai più gli eventi finché non lo ricollegherai.")) return;
    setBusy(true);
    try {
      await api.post("/crm/google/disconnect");
      toast.success("Google Calendar scollegato");
      loadStatus();
    } catch (e) { toast.error("Errore"); }
    finally { setBusy(false); }
  };

  const savePhone = async () => {
    setSavingPhone(true);
    try {
      await api.patch("/me/profile", { phone: waPhone });
      await refreshUser?.();
      toast.success("Numero WhatsApp salvato");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
    finally { setSavingPhone(false); }
  };

  const saveManagerPhone = async () => {
    setSavingManager(true);
    try {
      await api.patch("/me/profile", { manager_phone: managerPhone });
      await refreshUser?.();
      toast.success("Numero manager salvato — ora il bottone WhatsApp sulle note vocali è attivo");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore salvataggio"); }
    finally { setSavingManager(false); }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 md:p-10" data-testid="admin-setup">
      <header className="mb-8">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A1F]">
          <KeyRound size={14} /> Configurazione account
        </div>
        <h1 className="mt-2 font-display text-4xl md:text-5xl uppercase text-[#0A0A0A]">Setup</h1>
        <p className="mt-2 text-sm text-[#0A0A0A]/60 max-w-xl">Collega i tuoi strumenti di lavoro (Google Calendar, WhatsApp) e gestisci la password. Ogni venditore ha il suo setup indipendente.</p>
      </header>

      {/* GOOGLE CALENDAR */}
      <SetupSection
        icon={Calendar}
        title="Google Calendar"
        subtitle="Collega il TUO calendario Google (non quello di altri venditori). Le call che pianifichi qui saranno visibili nella tua agenda 'Il mio giorno'."
        testId="setup-calendar"
      >
        {!calStatus && <Loader2 size={16} className="animate-spin text-[#0A0A0A]/40" />}
        {calStatus && (
          <div>
            <StatusRow ok={calStatus.connected} label={calStatus.connected ? `Collegato: ${calStatus.email}` : "Non collegato"} />
            {calStatus.connected ? (
              <button onClick={disconnectGoogle} disabled={busy} className="mt-3 px-4 py-2 border-2 border-red-500 text-red-500 text-xs uppercase tracking-widest font-bold hover:bg-red-500 hover:text-white flex items-center gap-2 disabled:opacity-60" data-testid="btn-disconnect-google">
                {busy ? <Loader2 size={12} className="animate-spin"/> : <Unlink size={12}/>} Scollega
              </button>
            ) : (
              <button onClick={connectGoogle} disabled={busy || !calStatus.configured} className="mt-3 btn-cta disabled:opacity-60" data-testid="btn-connect-google">
                {busy ? <Loader2 size={12} className="animate-spin"/> : <Link2 size={12}/>} Collega Google Calendar
              </button>
            )}
            <InstructionsGoogle />
          </div>
        )}
      </SetupSection>

      {/* WHATSAPP */}
      <SetupSection
        icon={MessageCircle}
        title="WhatsApp"
        subtitle="I messaggi partono dal tuo device WhatsApp. Salva il tuo numero per riferimento e includilo nei template quando serve."
        testId="setup-whatsapp"
      >
        <div className="grid md:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Il tuo numero WhatsApp (con prefisso, es. +39...)</label>
            <input
              type="tel"
              value={waPhone}
              onChange={(e) => setWaPhone(e.target.value)}
              placeholder="+39 333 1234567"
              className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm font-mono"
              data-testid="setup-wa-phone"
            />
          </div>
          <button onClick={savePhone} disabled={savingPhone} className="btn-cta disabled:opacity-60" data-testid="setup-save-phone">
            {savingPhone ? <Loader2 size={12} className="animate-spin"/> : <Phone size={12}/>} Salva
          </button>
        </div>

        {/* Numero manager per inoltro note vocali */}
        <div className="mt-6 pt-6 border-t-2 border-[#0A0A0A]/10 grid md:grid-cols-[1fr_auto] gap-3 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F]">📤 Numero del manager (Alessio) — per inoltro note vocali</label>
            <input
              type="tel"
              value={managerPhone}
              onChange={(e) => setManagerPhone(e.target.value)}
              placeholder="+39 333 1234567"
              className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm font-mono"
              data-testid="setup-manager-phone"
            />
            <div className="text-[10px] text-[#0A0A0A]/60 mt-1">Ogni nota vocale che registri sulle schede lead avrà un bottone verde WhatsApp per inoltrarla direttamente a questo numero.</div>
          </div>
          <button onClick={saveManagerPhone} disabled={savingManager} className="btn-cta disabled:opacity-60" data-testid="setup-save-manager-phone">
            {savingManager ? <Loader2 size={12} className="animate-spin"/> : <Phone size={12}/>} Salva
          </button>
        </div>
        <InstructionsWhatsApp />
      </SetupSection>

      {/* PASSWORD */}
      <SetupSection
        icon={KeyRound}
        title="Password"
        subtitle="Se hai ricevuto una password temporanea dall'admin, cambiala con una tua qui sotto."
        testId="setup-password"
      >
        <button onClick={() => setPwdModal(true)} className="px-4 py-2 border-2 border-[#0A0A0A] text-xs uppercase tracking-widest font-bold hover:bg-[#0A0A0A] hover:text-white flex items-center gap-2" data-testid="btn-change-password">
          <KeyRound size={12} /> Cambia password
        </button>
      </SetupSection>

      {pwdModal && <ChangePasswordModal onClose={() => setPwdModal(false)} />}
    </div>
  );
}

function SetupSection({ icon: Icon, title, subtitle, children, testId }) {
  return (
    <section className="mb-6 border-2 border-[#0A0A0A] bg-[#FAFAF7]" data-testid={testId}>
      <div className="border-b-2 border-[#0A0A0A]/10 p-5 flex items-start gap-3">
        <div className="w-10 h-10 bg-[#FF5A1F] text-white flex items-center justify-center shrink-0">
          <Icon size={20} />
        </div>
        <div className="flex-1">
          <div className="font-display text-2xl uppercase text-[#0A0A0A]">{title}</div>
          <div className="text-sm text-[#0A0A0A]/60 mt-1">{subtitle}</div>
        </div>
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function StatusRow({ ok, label }) {
  return (
    <div className={`flex items-center gap-2 text-sm ${ok ? "text-green-700" : "text-[#0A0A0A]/60"}`}>
      {ok ? <CheckCircle2 size={16}/> : <XCircle size={16} className="text-red-500"/>}
      <span className="font-bold">{label}</span>
    </div>
  );
}

function InstructionsGoogle() {
  return (
    <details className="mt-5 border-l-4 border-[#FF5A1F] bg-[#FFF5EF] p-4 text-sm text-[#0A0A0A]/80">
      <summary className="cursor-pointer font-bold uppercase text-xs tracking-widest text-[#FF5A1F]">📖 Guida passo-passo</summary>
      <ol className="mt-3 space-y-2 list-decimal list-inside">
        <li>Clicca <b>"Collega Google Calendar"</b>.</li>
        <li>Verrai reindirizzato su una schermata Google — <b>usa il tuo account Google personale</b> (non quello di altri venditori).</li>
        <li>Nella schermata di consenso <b>spunta TUTTE le caselle</b>, in particolare <span className="bg-white px-1 border">"Visualizza gli eventi dei tuoi calendari Google"</span>. Se lasci vuoto, l'agenda non caricherà gli eventi.</li>
        <li>Al termine tornerai automaticamente qui. Se il collegamento è andato a buon fine vedrai il tuo indirizzo email sotto "Collegato".</li>
        <li>Da ora in poi, ogni evento del tuo calendario Google apparirà su <b>Il mio giorno</b> 15 minuti prima ti verrà mandato un promemoria automatico.</li>
      </ol>
      <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-500 p-2 text-[11px]">
        <b>⚠️ Importante:</b> se cambi Google account, prima <b>Scollega</b> e poi ricollega con l'account giusto — altrimenti l'agenda continuerà a mostrare il calendario precedente.
      </div>
    </details>
  );
}

function InstructionsWhatsApp() {
  return (
    <details className="mt-5 border-l-4 border-[#FF5A1F] bg-[#FFF5EF] p-4 text-sm text-[#0A0A0A]/80">
      <summary className="cursor-pointer font-bold uppercase text-xs tracking-widest text-[#FF5A1F]">📖 Come funziona l'invio WhatsApp</summary>
      <ol className="mt-3 space-y-2 list-decimal list-inside">
        <li>Ogni volta che clicchi un pulsante WhatsApp (in agenda, sulle schede o sui contratti) si apre <b>WhatsApp Web/App sul tuo device</b> con il messaggio pre-compilato.</li>
        <li>Il messaggio parte quindi dal <b>tuo numero WhatsApp personale</b>, non da un numero centralizzato.</li>
        <li>Il tuo <b>nome</b> viene inserito automaticamente nella firma dei messaggi (<code className="bg-white px-1">{"{sender}"}</code>) prendendo il primo nome dal tuo profilo.</li>
        <li>Il numero che salvi qui è di riferimento (usato in eventuali template avanzati e per la scheda venditore visibile all'admin).</li>
      </ol>
      <div className="mt-4 bg-blue-50 border-l-4 border-blue-500 p-2 text-[11px]">
        <b>💡 In arrivo:</b> quando Alessio attiverà Meta WhatsApp Cloud API l'invio diventerà 100% automatico e i contratti verranno allegati direttamente in chat senza aprire WhatsApp manualmente.
      </div>
    </details>
  );
}
